import React, { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import axios from 'axios'
import { CheckCircle, XCircle, Clock, Shield, User, Mail, AlertTriangle } from 'lucide-react'

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL
const API = `${BACKEND_URL}/api`

// Public invitation acceptance page
export function InvitationAcceptance() {
  const { token } = useParams()
  const navigate = useNavigate()
  const [invitation, setInvitation] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [accepting, setAccepting] = useState(false)
  const [accepted, setAccepted] = useState(false)
  
  const [formData, setFormData] = useState({
    name: '',
    password: '',
    confirmPassword: ''
  })
  const [formErrors, setFormErrors] = useState({})

  // Load invitation details
  useEffect(() => {
    const loadInvitation = async () => {
      try {
        setLoading(true)
        setError('')
        
        const { data } = await axios.get(`${API}/invitations/${token}`)
        setInvitation(data)
        
      } catch (err) {
        const message = err.response?.data?.detail || 'Failed to load invitation'
        setError(message)
        
        // If invitation is expired or invalid, show appropriate message
        if (err.response?.status === 400 || err.response?.status === 404) {
          // Let the error state handle the display
        }
      } finally {
        setLoading(false)
      }
    }
    
    if (token) {
      loadInvitation()
    } else {
      setError('Invalid invitation link')
      setLoading(false)
    }
  }, [token])

  // Validate form
  const validateForm = () => {
    const errors = {}
    
    if (!formData.name.trim()) {
      errors.name = 'Name is required'
    }
    
    if (!formData.password) {
      errors.password = 'Password is required'
    } else if (formData.password.length < 8) {
      errors.password = 'Password must be at least 8 characters'
    }
    
    if (formData.password !== formData.confirmPassword) {
      errors.confirmPassword = 'Passwords do not match'
    }
    
    setFormErrors(errors)
    return Object.keys(errors).length === 0
  }

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault()
    
    if (!validateForm()) {
      return
    }
    
    try {
      setAccepting(true)
      setError('')
      
      await axios.post(`${API}/invitations/${token}/accept`, {
        token,
        name: formData.name,
        password: formData.password
      })
      
      setAccepted(true)
      
      // Redirect to login after success
      setTimeout(() => {
        navigate('/auth/login', { 
          state: { 
            message: 'Account created successfully! Please log in with your new credentials.',
            email: invitation.email 
          }
        })
      }, 3000)
      
    } catch (err) {
      const message = err.response?.data?.detail || 'Failed to accept invitation'
      setError(message)
    } finally {
      setAccepting(false)
    }
  }

  const getPasswordStrength = (password) => {
    if (!password) return { score: 0, label: '', color: '' }
    
    let score = 0
    if (password.length >= 8) score++
    if (/[A-Z]/.test(password)) score++
    if (/[a-z]/.test(password)) score++
    if (/[0-9]/.test(password)) score++
    if (/[^A-Za-z0-9]/.test(password)) score++
    
    if (score <= 2) return { score, label: 'Weak', color: 'text-red-600' }
    if (score <= 3) return { score, label: 'Fair', color: 'text-yellow-600' }
    if (score <= 4) return { score, label: 'Good', color: 'text-blue-600' }
    return { score, label: 'Strong', color: 'text-green-600' }
  }

  const passwordStrength = getPasswordStrength(formData.password)

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-canvas flex items-center justify-center">
        <div className="max-w-md w-full mx-auto p-6">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
            <p className="text-muted">Loading invitation...</p>
          </div>
        </div>
      </div>
    )
  }

  // Error state
  if (error && !invitation) {
    return (
      <div className="min-h-screen bg-canvas flex items-center justify-center">
        <div className="max-w-md w-full mx-auto p-6">
          <div className="text-center">
            <XCircle size={48} className="mx-auto text-red-500 mb-4" />
            <h1 className="text-2xl font-bold text-primary mb-2">Invalid Invitation</h1>
            <p className="text-muted mb-6">{error}</p>
            <button 
              onClick={() => navigate('/')}
              className="btn primary"
            >
              Go to Homepage
            </button>
          </div>
        </div>
      </div>
    )
  }

  // Success state
  if (accepted) {
    return (
      <div className="min-h-screen bg-canvas flex items-center justify-center">
        <div className="max-w-md w-full mx-auto p-6">
          <div className="text-center">
            <CheckCircle size={48} className="mx-auto text-green-500 mb-4" />
            <h1 className="text-2xl font-bold text-primary mb-2">Welcome to SaaS-Base!</h1>
            <p className="text-muted mb-6">
              Your account has been created successfully. You'll be redirected to the login page in a moment.
            </p>
            <div className="animate-pulse text-sm text-muted">
              Redirecting to login...
            </div>
          </div>
        </div>
      </div>
    )
  }

  // Main invitation acceptance form
  return (
    <div className="min-h-screen bg-canvas flex items-center justify-center p-4">
      <div className="max-w-md w-full mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <span className="text-2xl font-bold text-primary">SB</span>
          </div>
          <h1 className="text-2xl font-bold text-primary mb-2">Join SaaS-Base</h1>
          <p className="text-muted">You've been invited to join our team</p>
        </div>

        {/* Invitation Details Card */}
        <div className="card p-6 mb-6">
          <div className="text-center mb-4">
            <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-3">
              <Mail className="text-blue-600" size={24} />
            </div>
            <h2 className="font-semibold text-primary">Invitation Details</h2>
          </div>
          
          <div className="space-y-3 text-sm">
            <div className="flex items-center justify-between">
              <span className="text-muted">Email:</span>
              <span className="font-medium">{invitation?.email}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-muted">Role:</span>
              <span className="font-medium">{invitation?.role_template_name}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-muted">MFA Required:</span>
              <span className={`font-medium ${invitation?.mfa_required ? 'text-green-600' : 'text-yellow-600'}`}>
                {invitation?.mfa_required ? 'Yes' : 'No'}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-muted">Expires:</span>
              <span className="font-medium">
                {invitation?.expires_at ? new Date(invitation.expires_at).toLocaleDateString() : 'Unknown'}
              </span>
            </div>
          </div>

          {invitation?.message && (
            <div className="mt-4 p-3 bg-blue-50 rounded-lg">
              <p className="text-sm italic text-blue-800">"{invitation.message}"</p>
            </div>
          )}
        </div>

        {/* Account Creation Form */}
        <div className="card p-6">
          <h3 className="font-semibold text-primary mb-4">Create Your Account</h3>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-primary mb-1">
                Full Name
              </label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted" size={16} />
                <input
                  type="text"
                  className={`input pl-10 w-full ${formErrors.name ? 'border-red-500' : ''}`}
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  placeholder="Enter your full name"
                />
              </div>
              {formErrors.name && (
                <p className="text-red-600 text-xs mt-1">{formErrors.name}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-primary mb-1">
                Password
              </label>
              <div className="relative">
                <Shield className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted" size={16} />
                <input
                  type="password"
                  className={`input pl-10 w-full ${formErrors.password ? 'border-red-500' : ''}`}
                  value={formData.password}
                  onChange={(e) => setFormData({...formData, password: e.target.value})}
                  placeholder="Create a strong password"
                />
              </div>
              {formData.password && (
                <div className="mt-1 flex items-center gap-2">
                  <div className="flex-1 bg-gray-200 rounded-full h-1">
                    <div 
                      className={`h-1 rounded-full transition-all ${
                        passwordStrength.score <= 2 ? 'bg-red-500' :
                        passwordStrength.score <= 3 ? 'bg-yellow-500' :
                        passwordStrength.score <= 4 ? 'bg-blue-500' : 'bg-green-500'
                      }`}
                      style={{ width: `${(passwordStrength.score / 5) * 100}%` }}
                    ></div>
                  </div>
                  <span className={`text-xs ${passwordStrength.color}`}>
                    {passwordStrength.label}
                  </span>
                </div>
              )}
              {formErrors.password && (
                <p className="text-red-600 text-xs mt-1">{formErrors.password}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-primary mb-1">
                Confirm Password
              </label>
              <div className="relative">
                <Shield className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted" size={16} />
                <input
                  type="password"
                  className={`input pl-10 w-full ${formErrors.confirmPassword ? 'border-red-500' : ''}`}
                  value={formData.confirmPassword}
                  onChange={(e) => setFormData({...formData, confirmPassword: e.target.value})}
                  placeholder="Confirm your password"
                />
              </div>
              {formErrors.confirmPassword && (
                <p className="text-red-600 text-xs mt-1">{formErrors.confirmPassword}</p>
              )}
            </div>

            {invitation?.mfa_required && (
              <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg flex items-start gap-2">
                <AlertTriangle className="text-yellow-600 mt-0.5" size={16} />
                <div className="text-sm">
                  <p className="font-medium text-yellow-800">MFA Setup Required</p>
                  <p className="text-yellow-700">
                    You'll be required to set up multi-factor authentication after creating your account.
                  </p>
                </div>
              </div>
            )}

            {error && (
              <div className="error" role="alert">
                {error}
              </div>
            )}

            <button
              type="submit"
              className="btn primary w-full"
              disabled={accepting}
            >
              {accepting ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Creating Account...
                </>
              ) : (
                <>
                  <CheckCircle size={16} className="mr-2" />
                  Accept Invitation & Create Account
                </>
              )}
            </button>
          </form>

          <div className="mt-6 text-center text-xs text-muted">
            By creating an account, you agree to SaaS-Base's Terms of Service and Privacy Policy.
          </div>
        </div>
      </div>
    </div>
  )
}

export default InvitationAcceptance